import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import fetchApi from '../../utils/api';
import '../../styles/AddExpensesModal.css'; // Reuse existing styles
import Swal from 'sweetalert2';
import AirDatePicker from '../ui/AirDatePicker';

function EditExpensesModal({ isOpen, onClose, expense, isPage }) {
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [allParties, setAllParties] = useState([]);
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [selectedType, setSelectedType] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Single expense row state
    const [row, setRow] = useState({
        partyType: '',
        partyId: '',
        partyName: '',
        date: '',
        expenseName: '',
        invoiceNo: '',
        expensePurpose: '',
        netAmount: 0,
        gstAmount: 0,
        rowTotal: 0
    });

    const [partySearch, setPartySearch] = useState('');
    const [partyDropdown, setPartyDropdown] = useState(false);
    const [partyActiveIndex, setPartyActiveIndex] = useState(-1);
    const dropdownRef = useRef(null);

    // Load initial data (Expense Types & Unified Accounts)
    useEffect(() => {
        const loadData = async () => {
            let typesList = [];
            // Load Expense Types
            try {
                const etRes = await fetchApi('/expense-type');
                typesList = etRes.expenseTypes || [];
                setExpenseTypes(typesList);
            } catch (err) {
                console.error("Load expense types error:", err);
            }

            // Load Unified Accounts/Parties
            try {
                const accountRes = await fetchApi('/account/unified?limit=1000');
                const accountParties = (accountRes.accounts || [])
                    .filter(a => {
                        const name = (a.partyName || a.account_name || '').toLowerCase();
                        return !name.includes('cash') && !name.includes('bank');
                    })
                    .map(a => ({
                        id: a.id,
                        name: a.partyName,
                        type: a.type || 'Account',
                        balance: parseFloat(a.balance) || 0
                    }));
                setAllParties(accountParties);
            } catch (err) {
                console.error("Load unified parties error:", err);
            }

            // Populate form when the target expense changes
            if (expense) {
                const etId = expense.expenseTypeId || expense.expense_type_id || '';
                setSelectedTypeId(String(etId));
                const foundType = typesList.find(x => String(x.id) === String(etId));
                setSelectedType(foundType || null);

                setRow({
                    partyType: expense.partyType || expense.party_type || '',
                    partyId: expense.partyId || expense.party_id || '',
                    partyName: expense.partyName || expense.party_name || '',
                    date: expense.date || '',
                    expenseName: expense.expenseName || expense.expense_name || '',
                    invoiceNo: expense.invoiceNo || expense.invoice_no || '',
                    expensePurpose: expense.expensePurpose || expense.expense_purpose || '',
                    netAmount: parseFloat(expense.netAmount || expense.net_amount) || 0,
                    gstAmount: parseFloat(expense.gstAmount || expense.gst_amount) || 0,
                    rowTotal: parseFloat(expense.rowTotal || expense.row_total) || 0
                });
                setPartySearch(expense.partyName || expense.party_name || '');
            }
        };

        if (isOpen || isPage) {
            loadData();
        }
    }, [isOpen, isPage, expense]);

    // Handle scroll of highlighted option in dropdown
    useEffect(() => {
        if (partyDropdown && partyActiveIndex >= 0 && dropdownRef.current) {
            const container = dropdownRef.current;
            const activeEl = container.children[partyActiveIndex];
            if (activeEl) {
                activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [partyActiveIndex, partyDropdown]);

    const recalcRow = (netAmt, type) => {
        const net = parseFloat(netAmt) || 0;
        const taxRate = ((parseFloat(type?.tax1) || 0) + (parseFloat(type?.tax2) || 0));
        const gst = parseFloat(((net * taxRate) / 100).toFixed(2));
        return { gstAmount: gst, rowTotal: parseFloat((net + gst).toFixed(2)) };
    };

    const handleTypeChange = (e) => {
        const id = e.target.value;
        setSelectedTypeId(id);
        const t = expenseTypes.find(x => String(x.id) === id);
        setSelectedType(t || null);
        
        if (t) {
            const calcs = recalcRow(row.netAmount, t);
            setRow(prev => ({ ...prev, ...calcs }));
        }
    };

    const handleFieldChange = (field, value) => {
        setRow(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'netAmount') {
                const calcs = recalcRow(value, selectedType);
                updated.gstAmount = calcs.gstAmount;
                updated.rowTotal = calcs.rowTotal;
            }
            return updated;
        });
    };

    // Filter parties based on search input
    const getFilteredParties = (search) => {
        if (!search || search.trim().length === 0) {
            return allParties.slice(0, 15);
        }
        const q = search.toLowerCase();
        return allParties.filter(p => p.name && p.name.toLowerCase().includes(q)).slice(0, 15);
    };

    const handlePartySearch = (value) => {
        setPartySearch(value);
        setPartyDropdown(true);
        setPartyActiveIndex(-1);
        setRow(prev => ({
            ...prev,
            partyName: value,
            partyId: '',
            partyType: ''
        }));
    };

    const selectParty = (party) => {
        setRow(prev => ({
            ...prev,
            partyId: party.id,
            partyName: party.name,
            partyType: party.type
        }));
        setPartySearch(party.name);
        setPartyDropdown(false);
        setPartyActiveIndex(-1);
    };

    const handleKeyDown = (e, field) => {
        if (field === 'partyName' && partyDropdown) {
            const filtered = getFilteredParties(partySearch);
            if (filtered.length > 0) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setPartyActiveIndex(prev => (prev + 1) % filtered.length);
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setPartyActiveIndex(prev => (prev - 1 + filtered.length) % filtered.length);
                } else if (e.key === 'Enter') {
                    if (partyActiveIndex >= 0 && partyActiveIndex < filtered.length) {
                        e.preventDefault();
                        selectParty(filtered[partyActiveIndex]);
                    }
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setPartyDropdown(false);
                    setPartyActiveIndex(-1);
                }
            }
        }
    };

    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        if (e) e.preventDefault();
        if (!selectedTypeId) { toast.error("Please select an Expense Type"); return; }
        if (!row.expenseName && !row.partyName && row.netAmount <= 0) { toast.error("Please fill the expense details"); return; }

        setSubmitting(true);
        try {
            const t = selectedType;
            await fetchApi(`/expense/${expense._id || expense.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    expenseTypeId: selectedTypeId,
                    expenseTypeName: t?.expenseTypeName || t?.expense_type_name || '',
                    tax1: t?.tax1 || 0,
                    tax2: t?.tax2 || 0,
                    partyType: row.partyType,
                    partyId: row.partyId,
                    partyName: row.partyName,
                    date: row.date,
                    expenseName: row.expenseName,
                    invoiceNo: row.invoiceNo,
                    expensePurpose: row.expensePurpose,
                    netAmount: row.netAmount,
                    gstAmount: row.gstAmount,
                    rowTotal: row.rowTotal
                })
            });
            toast.success("Expense updated successfully!");
            if (onClose) onClose();
        } catch (err) {
            toast.error(err.message || "Failed to update expense");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen && !isPage) return null;

    const inp = { width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', background: '#fff', boxSizing: 'border-box' };

    const formContent = (
        <div className={isPage ? "page-card" : "add-expenses-modal"} onClick={(e) => !isPage && e.stopPropagation()} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
            {!isPage && (
                <>
                    <button className="add-expenses-close-icon" onClick={onClose}>✕</button>
                    <h2 className="add-expenses-title">Update Expense</h2>
                </>
            )}
            {isPage && <div style={{ textAlign: 'center', fontSize: '22px', fontWeight: '700', color: '#1a2e44', marginBottom: '20px' }}>Update Expense</div>}

            <div className={isPage ? "page-card__body" : ""}>
                <form className="add-expenses-form" onSubmit={handleSubmit} style={isPage ? { padding: '0' } : {}}>
                    {/* Row 1: Expense Type selector */}
                    <div className="add-expenses-row">
                        <div className="add-expenses-field" style={{ gridColumn: 'span 3' }}>
                            <label className="add-expenses-label">Expense Type</label>
                            <select
                                value={selectedTypeId}
                                onChange={handleTypeChange}
                                className="add-expenses-select"
                            >
                                <option value="">-- Select Expense Type --</option>
                                {expenseTypes.map(t => (
                                    <option key={t.id} value={String(t.id)}>{t.expenseTypeName} ({t.tax1}% + {t.tax2}%)</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 2: Party, Date, Expense Name */}
                    <div className="add-expenses-row">
                        {/* Party A/c */}
                        <div className="add-expenses-field">
                            <label className="add-expenses-label">Party A/c</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    onKeyDown={(e) => handleKeyDown(e, 'partyName')}
                                    className="add-expenses-input"
                                    placeholder="Search party a/c..."
                                    value={partySearch}
                                    onChange={(e) => handlePartySearch(e.target.value)}
                                    onFocus={() => {
                                        setPartyDropdown(true);
                                        setPartyActiveIndex(-1);
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            setPartyDropdown(false);
                                            setPartyActiveIndex(-1);
                                        }, 250);
                                    }}
                                />
                                {row.partyType && (
                                    <span style={{ fontSize: '10px', background: row.partyType === 'seller' ? '#e6f4ea' : row.partyType === 'customer' ? '#e8f0fe' : '#fff3e0', color: '#555', padding: '1px 5px', borderRadius: '3px', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                        {row.partyType}
                                    </span>
                                )}
                                {partyDropdown && (
                                    <div
                                        ref={dropdownRef}
                                        style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                                    >
                                        {getFilteredParties(partySearch).map((p, pi) => {
                                            const isHighlighted = partyActiveIndex === pi;
                                            return (
                                                <div
                                                    key={pi}
                                                    onMouseDown={() => selectParty(p)}
                                                    onMouseEnter={() => setPartyActiveIndex(pi)}
                                                    onMouseLeave={() => setPartyActiveIndex(-1)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        borderBottom: '1px solid #f0f0f0',
                                                        background: isHighlighted ? '#edf4fc' : '#fff',
                                                        transition: 'background-color 0.1s ease'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', textAlign: 'left' }}>
                                                        <span style={{ fontWeight: '600', color: '#1a2e44', fontSize: '13px' }}>{p.name}</span>
                                                        <span style={{ fontSize: '11px', color: (p.group && p.group.toLowerCase().includes('expense')) || p.type === 'Account' || p.type === 'account' ? '#333' : (p.balance > 0 ? '#10b981' : p.balance < 0 ? '#ef4444' : '#666'), fontWeight: '500' }}>
                                                            {(() => {
                                                                const isExpense = p.group && p.group.toLowerCase().includes('expense');
                                                                if (isExpense || p.type === 'Account' || p.type === 'account') {
                                                                    return `Expense: ₹${Math.abs(p.balance).toFixed(2)}`;
                                                                }
                                                                if (p.balance > 0) {
                                                                    return `Receivable: ₹${p.balance.toFixed(2)}`;
                                                                }
                                                                if (p.balance < 0) {
                                                                    return `Payable: ₹${Math.abs(p.balance).toFixed(2)}`;
                                                                }
                                                                if (p.type === 'customer' || p.type === 'Customer') {
                                                                    return 'Receivable: ₹0.00';
                                                                }
                                                                if (p.type === 'seller' || p.type === 'Seller' || p.type === 'transporter' || p.type === 'Transporter') {
                                                                    return 'Payable: ₹0.00';
                                                                }
                                                                return `₹${parseFloat(p.balance).toFixed(2)}`;
                                                            })()}
                                                        </span>
                                                    </div>
                                                    <span style={{
                                                        fontSize: '11px',
                                                        color: isHighlighted ? '#1a2e44' : '#888',
                                                        textTransform: 'capitalize',
                                                        background: isHighlighted ? '#dbeafe' : '#f5f5f5',
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        fontWeight: isHighlighted ? '600' : 'normal'
                                                    }}>{(p.group && p.group.toLowerCase().includes('expense')) ? p.group : p.type}</span>
                                                </div>
                                            );
                                        })}
                                        {getFilteredParties(partySearch).length === 0 && partySearch && (
                                            <div style={{ padding: '10px', color: '#888', fontSize: '13px', textAlign: 'center' }}>No parties found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Date */}
                        <div className="add-expenses-field">
                            <label className="add-expenses-label">Date</label>
                            <AirDatePicker
                                className="add-expenses-input"
                                value={row.date}
                                onChange={(dateStr) => handleFieldChange('date', dateStr)}
                            />
                        </div>

                        {/* Expense Name */}
                        <div className="add-expenses-field">
                            <label className="add-expenses-label">Expense Name</label>
                            <input
                                className="add-expenses-input"
                                placeholder="Type name..."
                                value={row.expenseName}
                                onChange={(e) => handleFieldChange('expenseName', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Row 3: Invoice No, Purpose, Net Amount */}
                    <div className="add-expenses-row">
                        {/* Invoice No */}
                        <div className="add-expenses-field">
                            <label className="add-expenses-label">Invoice No</label>
                            <input
                                className="add-expenses-input"
                                placeholder="Invoice no..."
                                value={row.invoiceNo}
                                onChange={(e) => handleFieldChange('invoiceNo', e.target.value)}
                            />
                        </div>

                        {/* Expense Purpose */}
                        <div className="add-expenses-field">
                            <label className="add-expenses-label">Expense Purpose</label>
                            <input
                                className="add-expenses-input"
                                placeholder="Purpose..."
                                value={row.expensePurpose}
                                onChange={(e) => handleFieldChange('expensePurpose', e.target.value)}
                            />
                        </div>

                        {/* Net Amount */}
                        <div className="add-expenses-field">
                            <label className="add-expenses-label">Net Amount</label>
                            <input
                                type="number"
                                className="add-expenses-input"
                                style={{ textAlign: 'right' }}
                                value={row.netAmount}
                                min="0"
                                step="0.01"
                                onChange={(e) => handleFieldChange('netAmount', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Row 4: GST Amount, Row Total (calculated, read-only) */}
                    <div className="add-expenses-row">
                        {/* GST Amount */}
                        <div className="add-expenses-field" style={{ gridColumn: 'span 1' }}>
                            <label className="add-expenses-label">GST Amount (Calculated)</label>
                            <input
                                type="number"
                                className="add-expenses-input"
                                style={{ textAlign: 'right', background: '#f0f4f8', cursor: 'default' }}
                                value={row.gstAmount}
                                readOnly
                            />
                        </div>

                        {/* Row Total */}
                        <div className="add-expenses-field" style={{ gridColumn: 'span 2' }}>
                            <label className="add-expenses-label">Row Total (Calculated)</label>
                            <input
                                type="number"
                                className="add-expenses-input"
                                style={{ textAlign: 'right', background: '#e8f5e9', fontWeight: '600', cursor: 'default' }}
                                value={row.rowTotal}
                                readOnly
                            />
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="add-expenses-actions">
                        <button type="button" className="add-expenses-btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="add-expenses-btn-submit" disabled={submitting}>
                            {submitting ? "Updating..." : "Update"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    if (isPage) return formContent;

    return (
        <div className="add-expenses-modal-overlay" onClick={onClose}>
            {formContent}
        </div>
    );
}

export default EditExpensesModal;
